import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Clock3,
  Loader2,
  MailOpen,
} from "lucide-react";
import { useRef, useState } from "react";
import { ConsentText } from "@/components/consent-text";
import { Button } from "@/components/ui/button";
import { consentGrants, readAcceptance } from "@/lib/consent";
import {
  inviteQueryOptions,
  queryClient,
  useAcceptInviteMutation,
} from "@/lib/data";
import { retryIfCancelled } from "@/lib/query-client";
import { startSession } from "@/lib/session";
import { useOrganization } from "@/lib/use-organization";

export const Route = createFileRoute("/i/$slug")({
  component: InvitePage,
  loader: ({ params }) => {
    const options = inviteQueryOptions(params.slug);
    return retryIfCancelled(options.queryKey, () =>
      queryClient.ensureQueryData(options),
    );
  },
});

function InvitePage() {
  const { slug } = Route.useParams();
  const invite = Route.useLoaderData();
  const organization = useOrganization();
  const navigate = useNavigate();
  const acceptInvite = useAcceptInviteMutation(slug);
  const [error, setError] = useState<string | null>(null);
  const noticeRef = useRef<HTMLSpanElement>(null);

  async function handleAccept(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const response = await acceptInvite.mutateAsync(
        noticeRef.current
          ? [readAcceptance(noticeRef.current, consentGrants(organization))]
          : undefined,
      );
      void navigate(await startSession(response.token));
    } catch {
      setError("We couldn't accept this invitation. Please try again.");
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-[radial-gradient(ellipse_at_top,var(--brand-tint),transparent_70%)]"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pt-10 pb-10 sm:pt-16">
        <header className="flex flex-col items-center text-center">
          {organization.logo_url ? (
            <img
              src={organization.logo_url}
              alt={organization.name}
              className="mb-5 h-14 w-auto"
            />
          ) : (
            <p className="mb-5 text-xs font-medium tracking-wider text-primary uppercase">
              {organization.name}
            </p>
          )}

          <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MailOpen className="size-6" />
          </div>

          <h1 className="mt-4 font-heading text-[2rem] leading-tight font-semibold text-foreground">
            {invite.expired ? "Your invitation has expired" : "You're invited"}
          </h1>
          <p className="mt-2 max-w-xs text-[0.95rem] leading-relaxed text-muted-foreground">
            {invite.expired
              ? "Sign in with your phone number to pick up where you left off."
              : `Complete your profile for ${organization.name}.`}
          </p>
        </header>

        <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-elevated)] sm:p-6">
          {error && (
            <div className="mb-5 flex animate-in items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3 text-sm text-destructive fade-in slide-in-from-top-1">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {invite.expired ? (
            <ExpiredActions phoneNumber={invite.applicant.phone_number} />
          ) : (
            <form onSubmit={handleAccept} className="space-y-5">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                data-icon="inline-end"
                disabled={acceptInvite.isPending}
              >
                {acceptInvite.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Continuing...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-center text-sm text-muted-foreground">
                <Clock3 className="size-3.5" />
                Completing a profile won't affect your credit.
              </p>
            </form>
          )}
        </div>

        {!invite.expired && (
          <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
            <ConsentText agreedBy="continuing" ref={noticeRef} />
          </p>
        )}
      </div>
    </div>
  );
}

function ExpiredActions({ phoneNumber }: { phoneNumber: string }) {
  const stripped = phoneNumber.replace(/^\+1/, "");

  return (
    <div className="space-y-3">
      <Button asChild className="w-full" size="lg" data-icon="inline-end">
        <Link to="/" search={{ phone: stripped }}>
          Sign in
          <ArrowRight className="size-4" />
        </Link>
      </Button>
    </div>
  );
}
